import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { NavSections } from 'common/enums';
import { Notification } from 'common/notification';
import { Spinner } from 'common/spinner';
import { appActions, AppContext } from 'context';
import { Vault } from 'models/vault';
import { MetaTags } from 'models/meta-tags';
import { useHandleApiError } from 'hooks/use-handle-api-error';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { goToPage, RouteName } from 'routes';

import { VideoCallWaitingRoom } from 'pages/vault-detail/components/video-call-waiting-room';
import { classnames } from 'helpers/utils';
import styles from './join-video-call-guest.module.scss';

type ParamType = {
  vaultId: string;
  videoCallToken: string;
};

const JoinVideoCallGuest: React.FC = () => {
  const { vaultId, videoCallToken } = useParams<ParamType>();
  const { dispatch } = useContext(AppContext);
  const [vault, setVault] = useState<VaultType>(new Vault());

  const [fetching, setFetching] = useState(true);
  const [showError, setShowError] = useState(false);

  const setApiError = useHandleApiError(() => {
    setShowError(true);
    goToPage(RouteName.NotFoundRedirect);
  });

  const fetchVault = async () => {
    try {
      if (vaultId && videoCallToken) {
        const response = await VaultsController.getVideoCallVault(
          +vaultId,
          videoCallToken,
        );
        const { coverThumbnailUrl, title } = response;
        const description = `Get to know ${title} ’s life story`;

        dispatch({
          type: appActions.SET_META_TAGS,
          metaTags: new MetaTags({ description, image: coverThumbnailUrl }),
        });

        setVault(response);
      } else {
        goToPage(RouteName.NotFoundRedirect);
      }
    } catch (err: any) {
      setApiError(err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    dispatch({
      type: appActions.ACTUAL_SECTION,
      actualSection: NavSections.vaults,
    });
    fetchVault();
  }, []);

  const showErrorNotification = useCallback(
    () => <Notification handleClose={() => setShowError(false)} />,
    [showError],
  );

  return (
    <>
      {showError && showErrorNotification()}

      <div
        className={classnames(
          globalStyles.loggedMainContainer,
          styles.joinVideoCallGuest,
        )}
      >
        {!fetching ? (
          <div className={styles.waitingRoomWrapper}>
            <VideoCallWaitingRoom vault={vault} isGuest />
          </div>
        ) : (
          <Spinner />
        )}
      </div>
    </>
  );
};

export { JoinVideoCallGuest };
